import { SearchPriceInput } from '@/components/form/search-price-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LanguageCode } from '@/data/languages';
import { Info, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslatableInput } from '../translatable-input';
import { NonInteractiveRules } from './non-interactive-rules';
import type { OrderBumpOffer } from './validation';

interface OrderBumpOffersProps {
  offers: OrderBumpOffer[];
  onAddOffer: () => void;
  onUpdateOffer: (index: number, offer: OrderBumpOffer) => void;
  onDeleteOffer: (index: number) => void;
  storeId: string;
}

/**
 * OrderBumpOffers - Manages multiple order bump offer configurations
 * Maximum 2 offers allowed
 */
export const OrderBumpOffers: React.FC<OrderBumpOffersProps> = ({
  offers,
  onAddOffer,
  onUpdateOffer,
  onDeleteOffer,
  storeId,
}) => {
  const { t } = useTranslation('upsells');
  return (
    <div className="space-y-4">
      {offers.map((offer, index) => (
        <OrderBumpOfferCard
          key={index}
          offer={offer}
          index={index}
          onUpdate={(updatedOffer) => onUpdateOffer(index, updatedOffer)}
          onDelete={() => onDeleteOffer(index)}
          storeId={storeId}
        />
      ))}

      {offers.length < 2 && (
        <Button type="button" variant="outline" onClick={onAddOffer} className="w-full">
          <Plus className="mr-2 h-4 w-4" />
          {t('form.orderBumpOffers.addOffer')}
        </Button>
      )}

      {offers.length === 0 && (
        <p className="text-muted-foreground py-4 text-center text-sm">{t('form.orderBumpOffers.noOffers')}</p>
      )}
    </div>
  );
};

interface OrderBumpOfferCardProps {
  offer: OrderBumpOffer;
  index: number;
  onUpdate: (offer: OrderBumpOffer) => void;
  onDelete: () => void;
  storeId: string;
}

/**
 * OrderBumpOfferCard - Single order bump offer configuration
 */
const OrderBumpOfferCard: React.FC<OrderBumpOfferCardProps> = ({
  offer,
  index,
  onUpdate,
  onDelete,
  storeId,
}) => {
  const { t } = useTranslation('upsells');
  const [titleLanguage, setTitleLanguage] = React.useState<LanguageCode>('en');
  const [descriptionLanguage, setDescriptionLanguage] = React.useState<LanguageCode>('en');
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            {t('form.orderBumpOffers.offerNumber', { index: index + 1 })}
          </CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Product Selection */}
        <div className="space-y-2">
          <Label>{t('form.orderBumpOffers.productLabel')}</Label>
          <SearchPriceInput
            storeId={storeId}
            priceId={offer.priceId ?? undefined}
            includeInactive
            onChange={(selection) => {
              onUpdate({
                ...offer,
                productId: selection.productId,
                variantId: selection.variantId,
                priceId: selection.priceId,
              });
            }}
          />
          <p className="text-muted-foreground text-xs">{t('form.orderBumpOffers.productHelper')}</p>
        </div>

        {/* Offer Type */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>{t('form.orderBumpOffers.offerTypeLabel')}</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="text-muted-foreground h-4 w-4 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <ul className="list-inside list-disc space-y-1 text-sm">
                    <li>
                      <strong>{t('form.orderBumpOffers.offerTypes.primary')}:</strong>{' '}
                      {t('form.orderBumpOffers.offerTypeTooltip.primary')}
                    </li>
                    <li>
                      <strong>{t('form.orderBumpOffers.offerTypes.secondary')}:</strong>{' '}
                      {t('form.orderBumpOffers.offerTypeTooltip.secondary')}
                    </li>
                    <li>
                      <strong>{t('form.orderBumpOffers.offerTypes.vip')}:</strong>{' '}
                      {t('form.orderBumpOffers.offerTypeTooltip.vip')}
                    </li>
                    <li>
                      <strong>{t('form.orderBumpOffers.offerTypes.nonInteractive')}:</strong>{' '}
                      {t('form.orderBumpOffers.offerTypeTooltip.nonInteractive')}
                    </li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            value={offer.type}
            onValueChange={(value: 'primary' | 'secondary' | 'vip' | 'non_interactive') => {
              onUpdate({
                ...offer,
                type: value,
                // Auto-check precheck for non_interactive
                precheck: value === 'non_interactive' ? true : offer.precheck,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="primary">{t('form.orderBumpOffers.offerTypes.primary')}</SelectItem>
              <SelectItem value="secondary">{t('form.orderBumpOffers.offerTypes.secondary')}</SelectItem>
              <SelectItem value="vip">{t('form.orderBumpOffers.offerTypes.vip')}</SelectItem>
              <SelectItem value="non_interactive">
                {t('form.orderBumpOffers.offerTypes.nonInteractive')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Title Translations */}
        <TranslatableInput
          label={t('form.orderBumpOffers.titleLabel')}
          required
          value={offer.titleTrans}
          onChange={(value: Record<string, string>) => onUpdate({ ...offer, titleTrans: value })}
          selectedLanguage={titleLanguage}
          onLanguageChange={setTitleLanguage}
        />

        {/* Description Translations */}
        <TranslatableInput
          label={t('form.orderBumpOffers.descriptionLabel')}
          value={offer.descriptionTrans}
          onChange={(value: Record<string, string>) => onUpdate({ ...offer, descriptionTrans: value })}
          selectedLanguage={descriptionLanguage}
          onLanguageChange={setDescriptionLanguage}
          mode="textarea"
        />

        {/* Precheck (only for interactive types) */}
        {offer.type !== 'non_interactive' && (
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t('form.orderBumpOffers.precheckLabel')}</Label>
              <p className="text-muted-foreground text-xs">{t('form.orderBumpOffers.precheckDescription')}</p>
            </div>
            <Switch
              checked={offer.precheck}
              onCheckedChange={(checked) => onUpdate({ ...offer, precheck: checked })}
            />
          </div>
        )}

        {/* Quantity */}
        <div className="space-y-2">
          <Label>{t('form.orderBumpOffers.quantityLabel')}</Label>
          <Input
            type="number"
            min="1"
            max="100"
            value={offer.quantity}
            onChange={(e) => onUpdate({ ...offer, quantity: parseInt(e.target.value) || 1 })}
            className="w-32"
          />
          <p className="text-muted-foreground text-xs">{t('form.orderBumpOffers.quantityDescription')}</p>
        </div>

        {/* Override Image URL */}
        <div className="space-y-2">
          <Label>{t('form.orderBumpOffers.overrideImageLabel')}</Label>
          <Input
            type="url"
            placeholder={t('form.orderBumpOffers.overrideImagePlaceholder')}
            value={offer.overrideImageUrl || ''}
            onChange={(e) => onUpdate({ ...offer, overrideImageUrl: e.target.value || null })}
          />
          <p className="text-muted-foreground text-xs">
            {t('form.orderBumpOffers.overrideImageDescription')}
          </p>
        </div>

        {/* Display Settings */}
        <div className="space-y-4 border-t pt-4">
          <h4 className="text-sm font-medium">{t('form.orderBumpOffers.displaySettings')}</h4>

          {/* Display Price */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t('form.orderBumpOffers.displayPriceLabel')}</Label>
              <p className="text-muted-foreground text-xs">
                {t('form.orderBumpOffers.displayPriceDescription')}
              </p>
            </div>
            <Switch
              checked={offer.displayPrice}
              onCheckedChange={(checked) => onUpdate({ ...offer, displayPrice: checked })}
            />
          </div>

          {/* Display Compare-at Price */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t('form.orderBumpOffers.displayCompareAtPriceLabel')}</Label>
              <p className="text-muted-foreground text-xs">
                {t('form.orderBumpOffers.displayCompareAtPriceDescription')}
              </p>
            </div>
            <Switch
              checked={offer.displayCompareAtPrice}
              onCheckedChange={(checked) => onUpdate({ ...offer, displayCompareAtPrice: checked })}
            />
          </div>

          {/* Compare-at Price Discount */}
          {offer.displayCompareAtPrice && (
            <div className="ml-6 space-y-2">
              <Label>{t('form.orderBumpOffers.compareAtDiscountLabel')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={offer.compareAtPriceDiscount}
                  onChange={(e) =>
                    onUpdate({ ...offer, compareAtPriceDiscount: parseInt(e.target.value) || 20 })
                  }
                  className="w-24"
                />
                <span className="text-muted-foreground text-sm">
                  {t('form.orderBumpOffers.compareAtDiscountPercent')}
                </span>
              </div>
              <p className="text-muted-foreground text-xs">
                {t('form.orderBumpOffers.compareAtDiscountDescription')}
              </p>
            </div>
          )}
        </div>

        {/* Conditional Display Rules (available for all offer types) */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium">{t('form.orderBumpOffers.conditionalRules')}</h4>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="text-muted-foreground h-4 w-4 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{t('form.orderBumpOffers.conditionalRulesTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-muted-foreground text-xs">
            {t('form.orderBumpOffers.conditionalRulesDescription')}
          </p>
          <NonInteractiveRules
            rules={offer.rules}
            onUpdate={(rules) => onUpdate({ ...offer, rules })}
            storeId={storeId}
          />
        </div>
      </CardContent>
    </Card>
  );
};
